import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import {
  rolePermissionService,
  Permission,
} from '../../services/rolePermissionService';

const PermissionsManager: React.FC = () => {
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingPermission, setEditingPermission] = useState<Permission | null>(
    null
  );
  const [permissionName, setPermissionName] = useState('');

  useEffect(() => {
    loadPermissions();
  }, []);

  const loadPermissions = async () => {
    try {
      setLoading(true);
      const response = await rolePermissionService.getPermissions();
      setPermissions(response.data.data || []);
    } catch (error) {
      console.error('Error loading permissions:', error);
      setPermissions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (permission?: Permission) => {
    if (permission) {
      setEditingPermission(permission);
      setPermissionName(permission.name);
    } else {
      setEditingPermission(null);
      setPermissionName('');
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingPermission(null);
    setPermissionName('');
  };

  const handleSubmit = async () => {
    try {
      if (editingPermission) {
        await rolePermissionService.updatePermission(editingPermission.id, {
          name: permissionName,
        });
      } else {
        await rolePermissionService.createPermission({
          name: permissionName,
        });
      }
      handleCloseDialog();
      loadPermissions();
    } catch (error) {
      console.error('Error saving permission:', error);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this permission?')) {
      try {
        await rolePermissionService.deletePermission(id);
        loadPermissions();
      } catch (error) {
        console.error('Error deleting permission:', error);
      }
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant='h5'>Permissions Management</Typography>
        <Button
          variant='contained'
          color='primary'
          onClick={() => handleOpenDialog()}
        >
          Add New Permission
        </Button>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(3, 1fr)',
          },
          gap: 3,
        }}
      >
        {permissions.map(permission => (
          <Card key={permission.id} sx={{ width: '100%' }}>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography variant='h6'>{permission.name}</Typography>
                <Box>
                  <IconButton
                    size='small'
                    onClick={() => handleOpenDialog(permission)}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size='small'
                    onClick={() => handleDelete(permission.id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Box>
              <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
                Guard: {permission.guard_name}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Box>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>
          {editingPermission ? 'Edit Permission' : 'Create New Permission'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin='dense'
            label='Permission Name'
            fullWidth
            value={permissionName}
            onChange={e => setPermissionName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant='contained' color='primary'>
            {editingPermission ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PermissionsManager;
